import React, { useState, useEffect, FC, ChangeEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFormSteps } from '../../hooks/useFormSteps';
import Stepper from '../../components/ui/Stepper';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Loader from '../../components/ui/Loader';
import Select from '../../components/ui/Select';
import jobService from '../../services/job.service';
import { ApplyFormData, JobData, ApplyResult, StepDefinition } from '../../types/components';
import Toast from '../../components/ui/Toast';

const ApplyMultiStep: FC = () => {
  const { jobId } = useParams<{ jobId: string }>(); // jobId from URL params
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [job, setJob] = useState<JobData | null>(null);
  const [jobLoading, setJobLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  const steps: StepDefinition[] = [
    { title: 'Onboarding', description: 'Complete Payment' },
    { title: 'Basic Info', description: 'Your core persona' },
    { title: 'Work / Experience', description: 'Resume Upload' },
    { title: 'Skills & Location', description: 'Skills and location' },
    { title: 'Complete', description: 'Review and finish' },
  ];

  const initialFormData: ApplyFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    skills: '',
    experience: '',
    coverLetter: '',
  };

  const {
    currentStep,
    formData,
    nextStep,
    prevStep,
    updateFormData,
    isFirstStep,
    isLastStep,
  } = useFormSteps<ApplyFormData>(steps, initialFormData);

  // Fetch job details
  useEffect(() => {
    const fetchJob = async () => {
      if (!jobId) {
        setError('No job ID provided');
        setJobLoading(false);
        return;
      }

      try {
        setJobLoading(true);
        // Mock job service call
        const jobData: JobData = {
          id: jobId,
          title: 'Senior React Developer',
          company: 'GreenStack Labs',
          location: 'Remote • India',
          type: 'Full-time',
          salary: '₹22L – ₹35L CTC',
        };
        setJob(jobData);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load job details');
        console.error('Error fetching job:', err);
      } finally {
        setJobLoading(false);
      }
    };

    fetchJob();
  }, [jobId]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      nextStep();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      prevStep();
    }
  };

  const handleSubmit = async () => {
    if (!job) {
      setToast({ message: 'Job information not loaded. Please try again.', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const applicationData = {
        ...formData,
        jobId: job.id,
        jobTitle: job.title,
        company: job.company,
        appliedAt: new Date().toISOString(),
      };

      // Simulate job service call
      const result: ApplyResult = { success: true, message: 'Application submitted successfully!' };
      // const result = await jobService.submitApplication(jobId, applicationData);
      
      if (result.success) {
        setToast({ message: 'Application submitted successfully!', type: 'success' });
        navigate('/user/status');
      } else {
        setToast({ message: result.message || 'Failed to submit application. Please try again.', type: 'error' });
      }
    } catch (error: any) {
      console.error('Error submitting application:', error);
      setToast({ message: 'Error submitting application. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (fieldName: keyof ApplyFormData, e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      updateFormData({ [fieldName]: e.target.files[0] } as Partial<ApplyFormData>);
    }
  };

  const handlePaymentComplete = () => {
    setPaymentCompleted(true);
    // Auto-redirect to next step after 3 seconds
    setTimeout(() => {
      nextStep();
    }, 3000);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Complete Payment</h3>
            {paymentCompleted ? (
              <div className="text-center py-8">
                <div className="text-green-600 mb-4">
                  <svg className="mx-auto h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-green-600 mb-2">Payment Completed!</h4>
                <p className="text-gray-600 mb-4">Your payment has been processed successfully.</p>
                <p className="text-sm text-gray-500">Redirecting to next step in 3 seconds...</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-6">
                  <svg className="mx-auto h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Payment Required</h4>
                <p className="text-gray-600 mb-6">Complete your payment to proceed with the application process.</p>
                <Button onClick={handlePaymentComplete} className="w-full sm:w-auto">
                  Complete Payment
                </Button>
              </div>
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                name="firstName"
                value={formData.firstName || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ firstName: e.target.value })}
                required
              />
              <Input
                label="Last Name"
                name="lastName"
                value={formData.lastName || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ lastName: e.target.value })}
                required
              />
            </div>
            <Input
              label="Email"
              name="email"
              type="email"
              value={formData.email || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ email: e.target.value })}
              required
            />
            <Input
              label="Phone"
              name="phone"
              type="tel"
              value={formData.phone || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ phone: e.target.value })}
              required
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Resume Upload</h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="mt-4">
                <label htmlFor="resume-upload" className="cursor-pointer">
                  <span className="mt-2 block text-sm font-medium text-gray-900">
                    Upload your resume
                  </span>
                  <span className="mt-1 block text-sm text-gray-500">
                    PDF, DOC, DOCX up to 10MB
                  </span>
                </label>
                <input
                  id="resume-upload"
                  name="resume"
                  type="file"
                  className="sr-only"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange('resume', e)}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Skills & Location</h3>
            <Input
              label="Skills (comma separated)"
              name="skills"
              value={formData.skills || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ skills: e.target.value })}
              placeholder="JavaScript, React, Node.js"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience Level</label>
              <Select
                options={[
                  { value: '', label: 'Select experience level' },
                  { value: 'entry', label: 'Entry Level (0-2 years)' },
                  { value: 'mid', label: 'Mid Level (3-5 years)' },
                  { value: 'senior', label: 'Senior Level (6+ years)' },
                ]}
                value={formData.experience || ''}
                onChange={(val) => updateFormData({ experience: val })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cover Letter
              </label>
              <textarea
                name="coverLetter"
                rows={4}
                value={formData.coverLetter || ''}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateFormData({ coverLetter: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                placeholder="Tell us why you're interested in this position..."
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Review Your Application</h3>
            
            {/* Job Information */}
            {job && (
              <div className="bg-primary-teaGreen/10 p-4 rounded-lg border border-primary-teaGreen/20">
                <h4 className="font-medium mb-2 text-primary-black">Job Information</h4>
                <p><strong>Position:</strong> {job.title}</p>
                <p><strong>Company:</strong> {job.company}</p>
                <p><strong>Location:</strong> {job.location}</p>
                <p><strong>Type:</strong> {job.type}</p>
                <p><strong>Salary:</strong> {job.salary}</p>
              </div>
            )}
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Personal Information</h4>
              <p><strong>Name:</strong> {formData.firstName} {formData.lastName}</p>
              <p><strong>Email:</strong> {formData.email}</p>
              <p><strong>Phone:</strong> {formData.phone}</p>
              
              <h4 className="font-medium mb-2 mt-4">Skills & Experience</h4>
              <p><strong>Skills:</strong> {formData.skills || 'Not specified'}</p>
              <p><strong>Experience:</strong> {formData.experience || 'Not specified'}</p>
              
              {formData.coverLetter && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Cover Letter</h4>
                  <p className="text-sm text-gray-700 bg-white p-3 rounded border">
                    {formData.coverLetter}
                  </p>
                </div>
              )}
              
              <h4 className="font-medium mb-2 mt-4">Files</h4>
              <p><strong>Resume:</strong> {formData.resume?.name || 'Not uploaded'}</p>
              <p><strong>Photo:</strong> {formData.photo?.name || 'Not uploaded'}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading state while fetching job data
  if (jobLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader />
            <p className="mt-4 text-gray-600">Loading job details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if job loading failed
  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Job</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/jobs')}>
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Apply for {job?.title || 'Job'}
        </h1>
        <p className="text-gray-600">
          {job ? `Apply for ${job.title} at ${job.company}` : 'Complete the following steps to submit your application'}
        </p>
      </div>

      <div className="mb-8">
        <Stepper
          steps={steps}
          currentStep={currentStep}
          onStepClick={(step) => {
            // Allow navigation to completed steps
            if (step <= currentStep) {
              // Navigate to step
            }
          }}
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {renderStepContent()}

        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={isFirstStep}
          >
          {(toast) && (
            <Toast
              message={toast.message}
              type={toast.type}
              position="top-right"
              duration={2500}
              onClose={() => setToast(null)}
              show
            />
          )}
            Previous
          </Button>

          {isLastStep ? (
            <Button
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            >
              {loading ? <Loader size="sm" /> : 'Submit Application'}
            </Button>
          ) : (
            <Button onClick={handleNext}>
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplyMultiStep;
